import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosCapacitacionAPI, bomberosCompetenciaAPI, bomberosCompaniaAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
    GraduationCap,
    Plus,
    Search,
    ArrowLeft,
    Calendar,
    Clock,
    Users,
    CheckCircle,
    PlayCircle,
    XCircle,
    Eye,
    Edit,
    MapPin,
    FileText,
    Trash2,
    Building2,
    BookOpen,
    Upload,
} from 'lucide-react';

export default function BomberosCapacitacionesPage() {
    const navigate = useNavigate();
    const { tienePermiso } = useBomberosPermisos();
    const [loading, setLoading] = useState(true);
    const [capacitaciones, setCapacitaciones] = useState([]);
    const [competencias, setCompetencias] = useState([]);
    const [companias, setCompanias] = useState([]);
    const [filteredCapacitaciones, setFilteredCapacitaciones] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterEstado, setFilterEstado] = useState('all');
    const [filterNivel, setFilterNivel] = useState('all');
    const [filterCompania, setFilterCompania] = useState('all');
    const [filterMalla, setFilterMalla] = useState('all');

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        filterCapacitaciones();
    }, [capacitaciones, searchTerm, filterEstado, filterNivel, filterCompania, filterMalla]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [capsRes, compsRes, companiasRes] = await Promise.all([
                bomberosCapacitacionAPI.getAll(),
                bomberosCompetenciaAPI.getAll(),
                bomberosCompaniaAPI.getAll(),
            ]);
            setCapacitaciones(capsRes.data);
            setCompetencias(compsRes.data);
            setCompanias(companiasRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const filterCapacitaciones = () => {
        let filtered = [...capacitaciones];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(c =>
                c.nombre?.toLowerCase().includes(term) ||
                c.codigo?.toLowerCase().includes(term) ||
                c.instructor?.toLowerCase().includes(term)
            );
        }

        if (filterEstado !== 'all') {
            filtered = filtered.filter(c => c.estado === filterEstado);
        }

        if (filterNivel !== 'all') {
            filtered = filtered.filter(c => c.nivel_anb === filterNivel);
        }
        
        if (filterCompania !== 'all') {
            if (filterCompania === 'todas') {
                filtered = filtered.filter(c => !c.compania_id);
            } else {
                filtered = filtered.filter(c => c.compania_id === filterCompania);
            }
        }

        if (filterMalla !== 'all') {
            filtered = filtered.filter(c => (c.malla_curricular || []).includes(filterMalla));
        }

        setFilteredCapacitaciones(filtered);
    };

    const handleEliminar = async (capacitacion) => {
        if (!window.confirm(`¿Está seguro de eliminar la capacitación "${capacitacion.nombre}"?\n\nEsta acción marcará la capacitación como cancelada.`)) return;
        
        try {
            await bomberosCapacitacionAPI.delete(capacitacion.id);
            toast.success('Capacitación eliminada correctamente');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar');
        }
    };

    const getEstadoConfig = (estado) => {
        switch (estado) {
            case 'programada':
                return { icon: Calendar, color: 'bg-blue-100 text-blue-700 border-blue-300', label: 'Programada' };
            case 'en_curso':
                return { icon: PlayCircle, color: 'bg-green-100 text-green-700 border-green-300', label: 'En Curso' };
            case 'finalizada':
                return { icon: CheckCircle, color: 'bg-gray-100 text-gray-700 border-gray-300', label: 'Finalizada' };
            case 'cancelada':
                return { icon: XCircle, color: 'bg-red-100 text-red-700 border-red-300', label: 'Cancelada' };
            default:
                return { icon: Calendar, color: 'bg-gray-100 text-gray-700 border-gray-300', label: estado };
        }
    };

    const getNivelColor = (nivel) => {
        switch (nivel) {
            case 'inicial': return 'bg-green-500';
            case 'operativo': return 'bg-blue-500';
            case 'profesional': return 'bg-purple-500';
            default: return 'bg-gray-500';
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-12 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-48" />)}
                </div>
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-4 md:space-y-6 p-4 md:p-6 pt-20 md:pt-20">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="min-w-0">
                        <h1 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center gap-2">
                            <GraduationCap className="h-5 w-5 md:h-6 md:w-6 shrink-0" />
                            <span className="truncate">Capacitaciones</span>
                        </h1>
                        <p className="text-xs sm:text-sm text-muted-foreground">{filteredCapacitaciones.length} capacitaciones registradas</p>
                    </div>
                </div>
                <div className="flex gap-2 ml-10 sm:ml-0 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => navigate('/bomberos/malla-curricular')} data-testid="malla-btn" className="text-xs sm:text-sm">
                        <BookOpen className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Malla Curricular</span>
                        <span className="sm:hidden">Malla</span>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => navigate('/bomberos/capacitaciones/importar')} data-testid="importar-cap-btn" className="text-xs sm:text-sm">
                        <Upload className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Importar</span>
                        <span className="sm:hidden">Import.</span>
                    </Button>
                    {tienePermiso('crear_capacitaciones') && (
                        <Button size="sm" onClick={() => navigate('/bomberos/capacitaciones/nueva')} data-testid="nueva-capacitacion-btn" className="text-xs sm:text-sm">
                            <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">Nueva Capacitación</span>
                            <span className="sm:hidden">Nueva</span>
                        </Button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <Card className="border-2">
                <CardContent className="pt-4 md:pt-6 pb-4">
                    <div className="flex flex-col gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por nombre, código o instructor..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 text-sm"
                                data-testid="search-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <Select value={filterEstado} onValueChange={setFilterEstado}>
                                <SelectTrigger className="text-sm" data-testid="filter-estado">
                                    <SelectValue placeholder="Estado" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los Estados</SelectItem>
                                    <SelectItem value="programada">Programada</SelectItem>
                                    <SelectItem value="en_curso">En Curso</SelectItem>
                                    <SelectItem value="finalizada">Finalizada</SelectItem>
                                    <SelectItem value="cancelada">Cancelada</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={filterNivel} onValueChange={setFilterNivel}>
                                <SelectTrigger className="text-sm" data-testid="filter-nivel">
                                    <SelectValue placeholder="Nivel" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los niveles</SelectItem>
                                    <SelectItem value="postulante">Postulante</SelectItem>
                                    <SelectItem value="inicial">Inicial</SelectItem>
                                    <SelectItem value="operativo">Operativo</SelectItem>
                                    <SelectItem value="profesional">Profesional</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={filterCompania} onValueChange={setFilterCompania}>
                                <SelectTrigger className="text-sm" data-testid="filter-compania">
                                    <SelectValue placeholder="Compañía" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas las Capacitaciones</SelectItem>
                                    <SelectItem value="todas">Capacitaciones Generales</SelectItem>
                                    {companias.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={filterMalla} onValueChange={setFilterMalla}>
                                <SelectTrigger className="text-sm col-span-2" data-testid="filter-malla">
                                    <SelectValue placeholder="Malla" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas las mallas</SelectItem>
                                    <SelectItem value="2024">Malla 2024</SelectItem>
                                    <SelectItem value="2025">Malla 2025</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                <Card className="border">
                    <CardContent className="p-3 sm:pt-4 sm:pb-4 text-center">
                        <p className="text-xl sm:text-2xl font-bold text-blue-600">{capacitaciones.filter(c => c.estado === 'programada').length}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground">Programadas</p>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="p-3 sm:pt-4 sm:pb-4 text-center">
                        <p className="text-xl sm:text-2xl font-bold text-green-600">{capacitaciones.filter(c => c.estado === 'en_curso').length}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground">En curso</p>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="p-3 sm:pt-4 sm:pb-4 text-center">
                        <p className="text-xl sm:text-2xl font-bold text-gray-600">{capacitaciones.filter(c => c.estado === 'finalizada').length}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground">Finalizadas</p>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="p-3 sm:pt-4 sm:pb-4 text-center">
                        <p className="text-xl sm:text-2xl font-bold text-purple-600">
                            {capacitaciones.reduce((acc, c) => acc + (c.total_aprobados || 0), 0)}
                        </p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground">Aprobados</p>
                    </CardContent>
                </Card>
            </div>

            {/* Capacitaciones Grid */}
            {filteredCapacitaciones.length === 0 ? (
                <Card className="border-2">
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <GraduationCap className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No se encontraron capacitaciones con los filtros aplicados</p>
                        <Button variant="outline" className="mt-4" onClick={() => navigate('/bomberos/capacitaciones/nueva')}>
                            <Plus className="h-4 w-4 mr-2" />
                            Crear Primera Capacitación
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredCapacitaciones.map((cap) => {
                        const estadoConfig = getEstadoConfig(cap.estado);
                        const EstadoIcon = estadoConfig.icon;
                        const porcentajeAprobados = cap.total_inscritos > 0 
                            ? Math.round((cap.total_aprobados / cap.total_inscritos) * 100) 
                            : 0;

                        return (
                            <Card
                                key={cap.id}
                                className="border-2 hover:border-primary transition-colors cursor-pointer overflow-hidden"
                                onClick={() => navigate(`/bomberos/capacitaciones/${cap.id}`)}
                                data-testid={`capacitacion-${cap.codigo}`}
                            >
                                <CardContent className="p-4 md:pt-6">
                                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                                <Badge className={`${getNivelColor(cap.nivel_anb)} text-white capitalize text-xs`}>
                                                    {cap.nivel_anb}
                                                </Badge>
                                                {(cap.malla_curricular || []).map(m => (
                                                    <Badge key={m} variant="outline" className="text-[10px] border-amber-500 text-amber-700">
                                                        Malla {m}
                                                    </Badge>
                                                ))}
                                                {cap.es_aula_virtual && (
                                                    <Badge variant="outline" className="text-[10px] border-indigo-400 text-indigo-600">@Virtual</Badge>
                                                )}
                                                <span className="text-xs text-muted-foreground font-mono">{cap.codigo}</span>
                                            </div>
                                            <h3 className="font-bold text-base sm:text-lg line-clamp-2 sm:line-clamp-1">{cap.nombre}</h3>
                                        </div>
                                        <Badge className={`${estadoConfig.color} flex items-center gap-1 shrink-0 text-xs`}>
                                            <EstadoIcon className="h-3 w-3" />
                                            {estadoConfig.label}
                                        </Badge>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm mb-4">
                                        <div className="flex items-center gap-1 sm:gap-2 text-muted-foreground">
                                            <Calendar className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
                                            <span className="truncate">{cap.fecha_inicio} - {cap.fecha_fin}</span>
                                        </div>
                                        <div className="flex items-center gap-1 sm:gap-2 text-muted-foreground">
                                            <Clock className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
                                            <span>{cap.horas_duracion} horas</span>
                                        </div>
                                        <div className="flex items-center gap-1 sm:gap-2 text-muted-foreground col-span-2">
                                            <Building2 className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
                                            <span className="truncate">
                                                {cap.compania_id 
                                                    ? companias.find(c => c.id === cap.compania_id)?.nombre || 'Compañía específica'
                                                    : 'Todas las compañías'
                                                }
                                            </span>
                                        </div>
                                        {cap.instructor && (
                                            <div className="flex items-center gap-1 sm:gap-2 text-muted-foreground">
                                                <Users className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
                                                <span className="truncate">{cap.instructor}</span>
                                            </div>
                                        )}
                                        {cap.lugar && (
                                            <div className="flex items-center gap-1 sm:gap-2 text-muted-foreground">
                                                <MapPin className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
                                                <span className="truncate">{cap.lugar}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Progreso */}
                                    <div className="space-y-1">
                                        <div className="flex justify-between text-xs sm:text-sm">
                                            <span className="text-muted-foreground">Inscritos / Aprobados</span>
                                            <span className="font-medium">{cap.total_aprobados || 0} / {cap.total_inscritos || 0}</span>
                                        </div>
                                        <Progress value={porcentajeAprobados} className="h-2" />
                                    </div>

                                    {/* Actions */}
                                    <div className="mt-3 pt-3 border-t flex justify-center sm:justify-end gap-1 sm:gap-2">
                                        {tienePermiso('ver_capacitaciones') && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-xs"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/bomberos/capacitaciones/${cap.id}`);
                                                }}
                                            >
                                                <Eye className="h-4 w-4 mr-1" />
                                                Ver
                                            </Button>
                                        )}
                                        {tienePermiso('editar_capacitaciones') && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-xs"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/bomberos/capacitaciones/${cap.id}/editar`);
                                                }}
                                            >
                                                <Edit className="h-4 w-4 mr-1" />
                                                Editar
                                            </Button>
                                        )}
                                        {tienePermiso('eliminar_capacitaciones') && cap.estado !== 'cancelada' && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-destructive hover:text-destructive text-xs"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEliminar(cap);
                                                }}
                                                data-testid={`eliminar-cap-${cap.codigo}`}
                                            >
                                                <Trash2 className="h-4 w-4 mr-1" />
                                                <span className="hidden sm:inline">Eliminar</span>
                                            </Button>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
        </>
    );
}
